import { createContext, useContext, type ReactNode } from 'react'
import type { ModuleKey, PublicSiteConfig } from '@kcm/contracts'

/**
 * The resolved site, available to every screen without prop-drilling.
 *
 * Rendered only once the config has loaded, so consumers get a real config
 * rather than `null` — which is why the hook throws instead of returning
 * optional: a component reading site config outside the provider is a bug, not
 * a state to design a fallback for.
 */
const SiteContext = createContext<PublicSiteConfig | null>(null)

export function SiteProvider({ site, children }: { site: PublicSiteConfig; children: ReactNode }) {
  return <SiteContext.Provider value={site}>{children}</SiteContext.Provider>
}

export function useSite(): PublicSiteConfig {
  const site = useContext(SiteContext)
  if (!site) throw new Error('useSite outside SiteProvider')
  return site
}

/** Whether a module is switched on for this site (PLAN.md §7). */
export function useModule(name: ModuleKey): boolean {
  return useSite().modules.includes(name)
}
